import { useState, useEffect } from "react";

/**
 * Componente que renderiza una imagen optimizada con soporte responsive utilizando el elemento <picture>.
 * Genera automáticamente versiones de imagen para diferentes resoluciones a partir de una URL base.
 *
 * @param {Object} props
 * @param {string} props.url - URL original de la imagen.
 * @param {string} props.title - Texto alternativo para accesibilidad y SEO.
 *
 * Este componente está diseñado para trabajar con imágenes que siguen la convención de nombres:
 *  - imagen-300x169.jpg
 *  - imagen-768x432.jpg
 *  - imagen-1024x576.jpg
 *  - imagen.jpg (original)
 */
export default function ImageHTML({ url, title }) {
  const [useFallback, setUseFallback] = useState(false);
  const [imgError, setImgError] = useState(false);

  const urlParts = url.split('.');
  const extension = urlParts.pop();
  const baseUrl = urlParts.join('.');

  const smallSize = useFallback ? '300x169' : '480x270';
  const urlSmall = `${baseUrl}-${smallSize}.${extension}`;
  const url768x432 = `${baseUrl}-768x432.${extension}`;
  const url1024x576 = `${baseUrl}-1024x576.${extension}`;
  const urlFull = `${baseUrl}.${extension}`;

  // Validar la imagen del source activo proactivamente
  useEffect(() => {
    const testUrl = window.matchMedia('(max-width: 480px)').matches
      ? urlSmall
      : window.matchMedia('(max-width: 1024px)').matches
        ? url768x432
        : urlFull;
    const img = new Image();
    img.src = testUrl;
    img.onerror = () => {
      if (!useFallback) {
        setUseFallback(true);
      } else {
        setImgError(true);
      }
    };
  }, [urlSmall, url768x432, urlFull, useFallback]);

  const handleError = () => {
    if (!useFallback) {
      setUseFallback(true);
    } else {
      setImgError(true);
    }
  };

  if (imgError) {
    return <div className="amx-notas-homes-img--broken" role="img" aria-label={title} />;
  }

  return (
    <picture>
      <source
        srcSet={urlSmall}
        media="(max-width: 480px)"
      />
      <source
        srcSet={url768x432}
        media="(max-width: 1024px)"
      />
      <source
        srcSet={urlFull}
        media="(min-width: 1025px)"
      />
      <img
        loading="lazy"
        decoding="async"
        width="300"
        height="169"
        src={urlSmall}
        alt={title}
        srcSet={`${urlSmall} 300w, ${url1024x576} 1024w, ${url768x432} 768w, ${urlFull} 1200w`}
        sizes="(max-width: 480px) 100vw, (max-width: 1024px) 100vw, 1200px"
        className='amx-notas-homes-img attachment-medium size-medium wp-post-image'
        onError={handleError}
      />
    </picture>
  );
}

